import React, { useEffect, useState, useRef, useCallback } from 'react';
import { videoService } from '../services/videoService';
import { VideoData, AudienceType } from '../types/video.types';
import '../styles/VideoPublic.css';

const BASE_URL = process.env.REACT_APP_API_URL?.replace('/api/v1', '') ?? 'http://localhost:8000';

const isExternalUrl = (url: string) => /^https?:\/\//.test(url);

interface VideoPlayerProps {
    video: VideoData;
}

const PlayIcon: React.FC = () => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
        <polygon points="5,3 19,12 5,21" />
    </svg>
);

const VideoPlayer: React.FC<VideoPlayerProps> = ({ video }) => {
    const videoRef   = useRef<HTMLVideoElement>(null);
    const timerRef   = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [active,     setActive]     = useState(false);
    const [showReplay, setShowReplay] = useState(false);
    const [impressed,  setImpressed]  = useState(false);

    const clearTimer = () => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    };

    const handlePlay = useCallback(() => {
        setShowReplay(false);
        clearTimer();

        if (video.maxDurationSeconds) {
            timerRef.current = setTimeout(() => {
                videoRef.current?.pause();
                setShowReplay(true);
            }, video.maxDurationSeconds * 1000);
        }

        if (!impressed) {
            setImpressed(true);
            videoService.trackImpression(video._id).catch(() => {});
        }
    }, [video.maxDurationSeconds, video._id, impressed]);

    const handleReplay = useCallback(() => {
        const el = videoRef.current;
        if (!el) return;
        clearTimer();
        el.currentTime = 0;
        el.play().catch(() => {});
        setShowReplay(false);
    }, []);

    const handleActivate = useCallback(() => {
        setActive(true);
        // Let the DOM update before calling play
        setTimeout(() => {
            videoRef.current?.play().catch(() => {});
        }, 50);
    }, []);

    useEffect(() => () => clearTimer(), []);

    const src    = isExternalUrl(video.videoUrl)    ? video.videoUrl    : `${BASE_URL}${video.videoUrl}`;
    const poster = video.thumbnailUrl
        ? (isExternalUrl(video.thumbnailUrl) ? video.thumbnailUrl : `${BASE_URL}${video.thumbnailUrl}`)
        : undefined;

    if (isExternalUrl(video.videoUrl) && (video.videoUrl.includes('youtube') || video.videoUrl.includes('youtu.be') || video.videoUrl.includes('vimeo'))) {
        return (
            <div className="vdp-embed-wrap">
                <iframe
                    src={src}
                    title={video.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="vdp-iframe"
                />
            </div>
        );
    }

    /* ── Thumbnail-first: show poster + play button until the user clicks ── */
    if (!active) {
        return (
            <div className="vdp-player-wrap vdp-poster-wrap" onClick={handleActivate} role="button" tabIndex={0}
                 aria-label={`Play ${video.title}`}
                 onKeyDown={e => e.key === 'Enter' && handleActivate()}>
                {poster
                    ? <img src={poster} alt={video.title} className="vdp-poster-img" />
                    : <div className="vdp-poster-fallback" />
                }
                <div className="vdp-play-overlay">
                    <span className="vdp-play-btn" aria-hidden="true"><PlayIcon /></span>
                </div>
            </div>
        );
    }

    return (
        <div className="vdp-player-wrap">
            <video
                ref={videoRef}
                src={src}
                poster={poster}
                loop={video.loop}
                muted={video.muted}
                playsInline
                controls
                className="vdp-video"
                onPlay={handlePlay}
            />
            {showReplay && (
                <button className="vdp-replay-btn" onClick={handleReplay} aria-label="Replay video">
                    ↺ Replay
                </button>
            )}
            {video.maxDurationSeconds && (
                <div className="vdp-duration-badge">{video.maxDurationSeconds}s</div>
            )}
        </div>
    );
};

interface VideoPublicProps {
    audience?:        AudienceType;
    className?:       string;
    sectionTitle?:    string;
    sectionSubtitle?: string;
    eyebrowLabel?:    string;
}

export const VideoPublic: React.FC<VideoPublicProps> = ({
    audience       = 'ALL',
    className,
    sectionTitle    = 'Our Story in Motion',
    sectionSubtitle = 'Watch how Arte Craft India crafts quality across every product line.',
    eyebrowLabel    = 'FEATURED VIDEOS',
}) => {
    const [videos, setVideos]   = useState<VideoData[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        videoService.fetchPublic(audience)
            .then(({ data: res }) => {
                setVideos((res as any).data ?? []);
                setLoading(false);
            })
            .catch(() => {
                setVideos([]);
                setLoading(false);
            });
    }, [audience]);

    if (loading || videos.length === 0) return null;

    return (
        <section
            className={`vdp-section${className ? ` ${className}` : ''}`}
            aria-label="Promotional videos"
        >
            {/* Decorative aurora orbs — purely visual, aria-hidden */}
            <span className="vdp-aurora-orb vdp-aurora-orb--1" aria-hidden="true" />
            <span className="vdp-aurora-orb vdp-aurora-orb--2" aria-hidden="true" />
            <span className="vdp-aurora-orb vdp-aurora-orb--3" aria-hidden="true" />

            <div className="vdp-inner">

                {/* ── Section header ── */}
                <div className="vdp-header">
                    <span className="vdp-eyebrow">{eyebrowLabel}</span>
                    <h2 className="vdp-section-title">
                        {sectionTitle}
                        <span className="vdp-title-underline" aria-hidden="true" />
                    </h2>
                    <p className="vdp-section-subtitle">{sectionSubtitle}</p>
                </div>

                {/* ── Video grid ── */}
                <div className="vdp-grid">
                    {videos.map(video => (
                        <div key={video._id} className="vdp-card">
                            <VideoPlayer video={video} />
                            <div className="vdp-meta">
                                <h3 className="vdp-title">{video.title}</h3>
                                {video.description && <p className="vdp-desc">{video.description}</p>}
                            </div>
                        </div>
                    ))}
                </div>

            </div>
        </section>
    );
};
